import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';

import { UserApiActions } from '../../../@store/user/user.actions';
import {
  selectAddedUserError,
  selectFetchedUserRoles,
  selectFetchedUserRolesError,
  selectFetchedUserRolesLoading,
} from '../../../@store/user/user.selectors';
import { CONFIG } from '../../../config';
import { SafeAny } from '../../../types/safe-any';

@Component({
  selector: 'ciwc-add-user',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './add-user.component.html',
  styleUrls: ['./add-user.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddUserComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly store = inject(Store);
  readonly fetchedRoles = this.store.selectSignal(selectFetchedUserRoles);
  readonly addedUserError = this.store.selectSignal(selectAddedUserError);
  readonly userRolesLoading = this.store.selectSignal(selectFetchedUserRolesLoading);
  readonly userRolesError = this.store.selectSignal(selectFetchedUserRolesError);

  isCreatingUser = signal(false);
  roles = signal<SafeAny[]>([]);
  errorText = CONFIG.ERROR_MESSAGES.DEFAULT;

  userForm = this.fb.group({
    firstName: ['', [Validators.required]],
    lastName: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    title: [''],
    faxNumber: [''],
    roles: ['', [Validators.required]],
  });

  constructor() {
    effect(() => {
      this.roles.set(this.fetchedRoles());

      if (this.addedUserError()) {
        this.isCreatingUser.set(false);
      }
    });
  }

  ngOnInit(): void {
    this.fetchUserRoles();
  }

  fetchUserRoles(): void {
    const payload = { identifier: 'USERS_ROLES' };
    this.store.dispatch(UserApiActions.fetchUserRoles({ payload }));
  }

  onSubmit(): void {
    this.isCreatingUser.set(true);

    if (this.userForm.valid) {
      const userData: SafeAny = {
        ...this.userForm.value,
        roles: [this.userForm.value.roles],
      };

      this.store.dispatch(UserApiActions.addUser({ payload: userData }));
    }
  }

  onCancel(): void {
    this.router.navigate(['/users/active']);
  }
}
